import { useEffect, useMemo, useState } from "react";
import "./propertyRevenue.css";
import { hasAnyPermission } from "../../permissions";

function PropertyRevenue() {
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]); // fetched rows
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Permission #6 (READ PROPERTY-WISE TARGETS & WRITE REVENUE CALCULATOR)
  // is the only granular permission that grants delete here — #5 (read
  // only) does not.
  const loggedInRole = localStorage.getItem("loggedInRole");

  // Super Admin/Admin/OTA Manager retain full delete access, matching how
  // this page worked before granular permissions existed — only OTA
  // Executive is meant to be restricted here without an explicit grant.
  const canDeleteTarget =
    ["SUPER ADMIN", "ADMIN", "OTA MANAGER"].includes(loggedInRole) ||
    hasAnyPermission(["read_targets_write_revenue_calc"]);
  // Agent Name dropdown options - usernames of every user in Settings
  // whose role is OTA EXECUTIVE or SALES EXECUTIVE (same pattern as
  // Property List's Agent column — Sales Executive is agent-linkable
  // exactly like OTA Executive).
  const [agentOptions, setAgentOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchAgents = async () => {
      try {
        const res = await fetch("/api/users");
        const data = await res.json();
        if (cancelled) return;

        const otaExecutives = (Array.isArray(data) ? data : [])
          .filter((u) => u && u.username && ["OTA EXECUTIVE", "SALES EXECUTIVE"].includes(u.role))
          .map((u) => u.username);

        setAgentOptions(otaExecutives);
      } catch (err) {
        console.error("Failed to load agent list:", err);
      }
    };

    fetchAgents();

    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch saved submissions from MongoDB
  const fetchRows = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetch("/api/revenue-forecast");

      const data = await res.json();

      if (!res.ok) {
        setError(data?.message || "Failed to fetch data");
        setRows([]);
        return;
      }

      const mapped = (Array.isArray(data) ? data : []).map((doc) => ({
        id: doc._id,
        propertyName: doc.propertyName || "",
        annualTarget: doc.annualTarget || "",
        occupancyPercentage: doc.occupancyPercentage || "",
        // Agent Name - the OTA EXECUTIVE this target is assigned to.
        // Older records saved before this feature existed simply have no
        // value here yet; the dropdown lets you assign one directly.
        assignedTo: doc.assignedTo || "",
      }));

      setRows(mapped);
    } catch (err) {
      console.error(err);
      setError("Server not reachable");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  // Search filter
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.propertyName || "").toLowerCase().includes(q));
  }, [search, rows]);

  const handleViewTargets = (id) => {
    window.location.href = `/view-targets/${id}`;
  };

  // Assign/update the agent for a single property target, directly from
  // this list - works the same for records that already had an agent and
  // ones (typically older records) that never had one assigned yet.
  const handleAgentChange = async (id, newAgent) => {
    const previousRows = rows;

    // Optimistic update
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, assignedTo: newAgent } : r))
    );

    try {
      const res = await fetch(`/api/revenue-forecast/${id}/agent`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assignedTo: newAgent }),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data?.message || "Failed to update agent.");
        setRows(previousRows); // revert on failure
        return;
      }
    } catch (err) {
      console.error("Update agent error:", err);
      alert("Server not reachable. Reverting change.");
      setRows(previousRows);
    }
  };

  // DELETE handler (asks confirmation first)
  const handleDelete = async (id, propertyName) => {
    const ok = window.confirm(
      `Are you sure you want to delete this property's target?\n\nProperty's Name: ${propertyName || "(No name)"}`
    );
    if (!ok) return;

    try {
      // Optimistic UI: remove immediately
      setRows((prev) => prev.filter((r) => r.id !== id));

      const res = await fetch(`/api/revenue-forecast/${id}`, {
        method: "DELETE",
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        alert(data?.message || "Failed to delete. Restoring list...");
        fetchRows();
        return;
      }

      alert("Deleted successfully ✅");
    } catch (err) {
      console.error(err);
      alert("Server not reachable. Restoring list...");
      fetchRows();
    }
  };

  // When no data, show a single empty row
  const displayRows =
    filtered.length > 0
      ? filtered
      : [{ id: "empty", propertyName: "", annualTarget: "", occupancyPercentage: "", assignedTo: "" }];

  return (
    <main className="rrc-property-list-main">
      <header className="rrc-property-header">
        <h1 className="rrc-property-title">PROPERTY TARGETS LIST</h1>
        <p className="rrc-property-subtitle">
          Search and open saved revenue targets property-wise.
        </p>
      </header>

      <section className="rrc-property-panel">
        <div className="rrc-property-search">
          <input
            type="text"
            placeholder="Search property..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading && <div className="rrc-property-hint">Loading saved properties...</div>}
        {!loading && error && <div className="rrc-property-error">{error}</div>}

        <div className="rrc-property-table-wrap">
          <table className="rrc-property-table">
            <thead>
              <tr>
                <th>Property Name</th>
                <th>Annual Target Revenue</th>
                <th>Overall Occupancy</th>
                <th>Agent Name</th>
                <th style={{ width: "260px", textAlign: "right" }}></th>
              </tr>
            </thead>

            <tbody>
              {displayRows.map((r) => (
                <tr key={r.id}>
                  <td>{r.propertyName || ""}</td>
                  <td>
                    {r.annualTarget
                      ? `₹ ${Number(r.annualTarget).toLocaleString("en-IN")}`
                      : ""}
                  </td>
                  <td>{r.occupancyPercentage ? `${r.occupancyPercentage}%` : ""}</td>

                  <td>
                    {r.id !== "empty" && (
                      <select
                        className="agent-select"
                        value={r.assignedTo || ""}
                        onChange={(e) => handleAgentChange(r.id, e.target.value)}
                        style={{
                          padding: "6px 8px",
                          borderRadius: "6px",
                          border: "1px solid #ccc",
                          cursor: "pointer",
                          backgroundColor: r.assignedTo ? "#dbeafe" : "#f3f4f6",
                          color: r.assignedTo ? "#1e40af" : "#6b7280",
                        }}
                      >
                        <option value="">Unassigned</option>
                        {agentOptions.map((agent) => (
                          <option key={agent} value={agent}>
                            {agent}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>

                  <td style={{ textAlign: "right" }}>
                    {r.id !== "empty" && (
                      <div className="rrc-property-actions">
                        <button
                          className="rrc-property-btn"
                          type="button"
                          onClick={() => handleViewTargets(r.id)}
                        >
                          VIEW TARGETS
                        </button>

                        {canDeleteTarget && (
                          <button
                            className="rrc-property-btn danger"
                            type="button"
                            onClick={() => handleDelete(r.id, r.propertyName)}
                          >
                            DELETE
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!loading && rows.length === 0 && !error && (
            <div className="rrc-property-hint">No saved targets yet.</div>
          )}
        </div>
      </section>

      <div className="copyright_footer">
        © 2026 RevX Hospitality. All Rights Reserved. Developed by <span style={{ color: 'green' }}>RevX Hospitality</span>.
      </div>
    </main>
  );
}

export default PropertyRevenue;
